/* mx-size.js — Shopify Size Modal, variants-aware */
(function () {
  if (window.__mxSizeLoaded) return;
  window.__mxSizeLoaded = true;

  const t = (key, fallback) => {
    try {
      const dict = window.theme?.strings || window.Ritual?.strings || {};
      return (
        key
          .split('.')
          .reduce((a, k) => (a && a[k] != null ? a[k] : null), dict) || fallback
      );
    } catch {
      return fallback;
    }
  };

  const STR = {
    title: t('products.product.choose_size', 'CHOOSE YOUR SIZE'),
    hint: t('products.product.size_hint', 'Only available sizes are shown'),
  };

  /** Utilities **/
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const dispatch = (el, type = 'change') =>
    el && el.dispatchEvent(new Event(type, { bubbles: true, cancelable: true }));

  /** Finds the main product form / option inputs on the PDP **/
  function findProductContext() {
    const form =
      qs('form[action*="/cart/add"]') ||
      qs('product-form form') ||
      qs('form#product-form');
    const optionInputs = qsa('[name^="options["]', form) // select/radio groups
      .concat(qsa('fieldset[name^="options["]', form));
    const addBtn =
      qs('[name="add"], button.add-to-cart-button, button[type="submit"]', form) ||
      qs('.product-form-buttons .button');

    return { form, optionInputs, addBtn };
  }

  /** Load product JSON from inline script or via /product-handle.js **/
  async function loadProductJSON() {
    const inline = qs(
      'script[type="application/json"][data-product], script[type="application/json"][data-product-json]'
    );
    if (inline) {
      try {
        return JSON.parse(inline.textContent.trim());
      } catch {}
    }
    // Fallback to Shopify endpoint
    const res = await fetch(`${location.pathname.replace(/\/$/, '')}.js`);
    if (!res.ok) throw new Error('Product JSON not found');
    return res.json();
  }

  /** Build modal shell if missing **/
  function ensureModalShell() {
    if (qs('#mxSizeModal')) return qs('#mxSizeModal');

    const dlg = document.createElement('dialog');
    dlg.id = 'mxSizeModal';
    dlg.className = 'mx-size-modal';
    dlg.innerHTML = `
      <h3 style="margin:0">${STR.title}</h3>
      <div id="mxSizeGrid" class="mx-size-grid" role="radiogroup" aria-label="${STR.title}"></div>
      <div class="mx-size-actions">
        <button id="mxSizeClose" class="mx-link" type="button">${t('actions.close', 'Close')}</button>
        <span class="paragraph" style="opacity:.7">${STR.hint}</span>
      </div>
      <button aria-label="${t(
        'actions.close_dialog',
        'Close dialog'
      )}" type="button"
              style="position:absolute;right:12px;top:12px;background:transparent;border:0;font:inherit;cursor:pointer"
              id="mxSizeX">×</button>
    `;
    document.body.appendChild(dlg);

    // Close handlers
    const close = () => dlg.close();
    qs('#mxSizeClose', dlg).addEventListener('click', close);
    qs('#mxSizeX', dlg).addEventListener('click', close);
    dlg.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });

    return dlg;
  }

  /** Given current option selections (except size), compute which size values are available **/
  function computeSizeAvailability(product, optionIndexByName, currentOptions) {
    // currentOptions is array by index (0..n-1)
    const sizeIdx = optionIndexByName.get('size'); // lowercased name match
    if (sizeIdx == null) return { order: [], availableSet: new Set() };

    const order = (product.options[sizeIdx]?.values || []).map((v) =>
      typeof v === 'string' ? v : v.value
    );
    const availableSet = new Set();

    product.variants.forEach((v) => {
      if (!v.available) return;
      // Compare all non-size options
      let match = true;
      for (let i = 0; i < product.options.length; i++) {
        if (i === sizeIdx) continue;
        const optVal = (v[`option${i + 1}`] || '').toString();
        const curVal = (currentOptions[i] || '').toString();
        if (curVal && optVal && optVal !== curVal) {
          match = false;
          break;
        }
      }
      if (match) {
        const sizeVal = (v[`option${sizeIdx + 1}`] || '').toString();
        if (sizeVal) availableSet.add(sizeVal);
      }
    });

    return { order, availableSet };
  }

  /** Get map: option name (lowercased) -> index **/
  function mapOptionIndexes(product) {
    const m = new Map();
    product.options.forEach((opt, idx) =>
      m.set((opt.name || '').toLowerCase(), idx)
    );
    return m;
  }

  /** Read current selections from hidden/native inputs **/
  function readCurrentSelections(ctx, product, optionIndexByName) {
    const arr = new Array(product.options.length).fill('');
    ctx.optionInputs.forEach((node) => {
      // selects
      if (node.tagName === 'SELECT') {
        const name = node.name.match(/\[([^\]]+)\]/)?.[1] || '';
        const idx = optionIndexByName.get(name.toLowerCase());
        if (idx != null) arr[idx] = node.value;
      } else if (node.matches('fieldset[name^="options["]')) {
        const name =
          node.getAttribute('name').match(/\[([^\]]+)\]/)?.[1] || '';
        const idx = optionIndexByName.get(name.toLowerCase());
        const checked = qs('input[type="radio"]:checked', node);
        if (idx != null && checked) arr[idx] = checked.value;
      } else if (node.matches('input[name^="options["]')) {
        // radio outside fieldset
        const name = node.name.match(/\[([^\]]+)\]/)?.[1] || '';
        const idx = optionIndexByName.get(name.toLowerCase());
        const checked = node.checked;
        if (idx != null && checked) arr[idx] = node.value;
      }
    });
    return arr;
  }

  /** Set size value into the native picker and trigger change **/
  function setSize(ctx, product, optionIndexByName, value) {
    const sizeIdx = optionIndexByName.get('size');
    if (sizeIdx == null) return;

    // Try select first
    const sizeSelect = qs(
      `select[name="options[${product.options[sizeIdx].name}]"]`,
      ctx.form
    );
    if (sizeSelect) {
      sizeSelect.value = value;
      dispatch(sizeSelect);
      return;
    }
    // Try radios (fieldset or inline)
    const radio = qs(
      `fieldset[name="options[${product.options[sizeIdx].name}]"] input[type="radio"][value="${CSS.escape(
        value
      )}"], input[name="options[${product.options[sizeIdx].name}]"][type="radio"][value="${CSS.escape(
        value
      )}"]`,
      ctx.form
    );
    if (radio) {
      radio.checked = true;
      dispatch(radio, 'input');
      dispatch(radio, 'change');
    }
  }

    /** Build / refresh the size buttons **/
  function renderSizes(dlg, product, ctx) {
    const optionIndexByName = mapOptionIndexes(product);
    const current = readCurrentSelections(ctx, product, optionIndexByName);
    const { order, availableSet } = computeSizeAvailability(
      product,
      optionIndexByName,
      current
    );
    const sizeIdx = optionIndexByName.get('size');
    if (sizeIdx == null) return;

    const currentSize = current[sizeIdx];
    const grid = qs('#mxSizeGrid', dlg);
    grid.innerHTML = '';

    // 1) Tallas reales
    order.forEach((sizeVal) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mx-size-btn';
      btn.textContent = sizeVal;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(sizeVal === currentSize));

      if (!availableSet.has(sizeVal)) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      }

      btn.addEventListener('click', () => {
        if (btn.disabled) return;

        setSize(ctx, product, optionIndexByName, sizeVal);

        // Solo resetea radios (no toca el botón SR)
        qsa('.mx-size-btn[role="radio"]', grid).forEach((b) =>
          b.setAttribute('aria-checked', 'false')
        );
        btn.setAttribute('aria-checked', 'true');

        dlg.close();
        ctx.addBtn && ctx.addBtn.focus();
      });

      grid.appendChild(btn);
    });

    // 2) Botón especial Size Recommender (NO es variante)
    const srBtn = document.createElement('button');
    srBtn.type = 'button';
    srBtn.className = 'mx-size-btn mx-size-btn--sr';
    srBtn.textContent = 'Size Recommender';
    srBtn.setAttribute('role', 'button');

    srBtn.addEventListener('click', () => {
      dlg.close();
      window.dispatchEvent(new CustomEvent('mx:open-size-recommender'));
    });

    grid.appendChild(srBtn);
  }


  /** Gate the Add to cart until size is selected **/
  function installAddToCartInterceptor(dlg, product, ctx) {
    if (!ctx.addBtn) return;
    if (ctx.addBtn.__mxWired) return;
    ctx.addBtn.__mxWired = true;

    const optionIndexByName = mapOptionIndexes(product);
    const sizeIdx = optionIndexByName.get('size');
    if (sizeIdx == null) return; // product doesn’t have Size option

    ctx.addBtn.addEventListener('click', (e) => {
      const current = readCurrentSelections(ctx, product, optionIndexByName);
      const sizeSelected = !!current[sizeIdx];
      if (!sizeSelected) {
        e.preventDefault();
        e.stopPropagation();
        renderSizes(dlg, product, ctx);
        dlg.showModal();
      }
    });
  }

  /** Re-render sizes when non-size options change (e.g., Color) **/
  function watchOptionChanges(dlg, product, ctx) {
    ctx.optionInputs.forEach((input) => {
      if (input.tagName === 'SELECT') {
        input.addEventListener('change', () => renderSizes(dlg, product, ctx));
      } else if (input.matches('fieldset[name^="options["]')) {
        input.addEventListener('change', () => renderSizes(dlg, product, ctx));
      } else if (input.matches('input[name^="options["]')) {
        input.addEventListener('change', () => renderSizes(dlg, product, ctx));
      }
    });
  }

  /** Hide original size picker visually (but keep it functional) **/
  function hideNativeSizePicker(product, ctx) {
    const sizeOpt = product.options.find(
      (o) => (o.name || '').toLowerCase() === 'size'
    );
    if (!sizeOpt) return;
    const sel = qs(`select[name="options[${sizeOpt.name}]"]`, ctx.form);
    const radios = qsa(
      `fieldset[name="options[${sizeOpt.name}]"], input[name="options[${sizeOpt.name}]"][type="radio"]`,
      ctx.form
    );
    sel && sel.classList.add('mx-size-original-hidden');
    radios.forEach((r) => {
      const holder = r.closest('fieldset') || r.closest('label') || r;
      holder.classList.add('mx-size-original-hidden');
    });
  }

  /** Init **/
  (async function init() {
    const ctx = findProductContext();
    if (!ctx.form) return; // not on product page

    let product = null;
    try {
      product = await loadProductJSON();
    } catch (e) {
      console.warn('[mx-size] could not load product JSON', e);
      return;
    }

    const hasSize = product.options.some(
      (o) => (o.name || '').toLowerCase() === 'size'
    );
    if (!hasSize) return; // product without Size option

    const dlg = ensureModalShell();
    hideNativeSizePicker(product, ctx);
    renderSizes(dlg, product, ctx);
    watchOptionChanges(dlg, product, ctx);
    installAddToCartInterceptor(dlg, product, ctx);

    // Expose a tiny debug helper (optional)
    window.__mxDebug = {
      product,
      openModal: () => {
        renderSizes(dlg, product, ctx);
        dlg.showModal();
      },
    };

    console.log('[mx-size] listo');
  })();
})();
